import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import {
  emptyEditLine,
  ensureTrailingRows,
  findAccount,
  forgetLineClassification,
  hasClassifiedProfitLines,
  isEmptyEditLine,
  normaliseEditLine,
  parseAmount,
  sanitiseAmountInput,
  type TrialBalanceEditLine,
  type TrialBalanceEditModel,
} from '@/core/models/trial-balance.models';
import { Button } from '@/kit/Button';
import { openDialog } from '@/kit/dialog';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { trialBalanceService } from '@/core/services/trialBalance';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { SelectPanel } from '@/kit/SelectPanel';
import {
  accountOptionsSignature,
  buildAccountOptions,
  isLastLine,
  type AccountPickOption,
  type EditModel,
} from './trialBalanceEditor';
import { TrialBalanceActions } from './TrialBalanceActions';
import { AddNewAccount, type AddNewAccountDialogData } from './AddNewAccount';
import { loadChartAccounts } from './trialBalanceEditor';
import { useAccountSuggestions } from './useAccountSuggestions';
import './TrialBalanceBackdetails.scss';

interface TrialBalanceBackdetailsProps {
  /** The shared model; changed only through `onEdit` (the editor applies the change and refreshes every panel). */
  model: TrialBalanceEditModel;
  /** Bumped by the parent on every change so this panel refreshes (the model object never changes). */
  revision?: number;
  onEdit: EditModel;
  /** task-120: a one-click way out of the "no chart of accounts" dead end, not just an explanation of it. */
  onOpenChartOfAccounts: () => void;
  /**
   * BUG-040: adds an account to the client's chart without leaving the trial balance (Chart of Accounts > Add New
   * Account), filled in from `searchTerm`. Resolves with the new account's code once it can be picked on a line, or
   * null when nothing was added.
   */
  onAddAccount?: (searchTerm: string) => Promise<string | null>;
  /** BUG-036: the old grid's Save - stores the trial balance only when it is balanced. */
  onSave?: () => void;
  /** BUG-036: the old grid's Cancel - leaves without storing the unsaved changes. */
  onCancel?: () => void;
  /** BUG-036: a Save is in progress (both buttons wait for it). */
  isSaving?: boolean;
}

/**
 * Amount columns are DECIMAL(19,4): at most 15 whole digits and 4 decimals. Pasted commas, pound signs and spaces stay
 * (the on-blur normalise strips them); only digits beyond the limits are dropped.
 */
const AMOUNT_MAX_INT_DIGITS = 15;
const AMOUNT_MAX_DECIMALS = 4;
function capAmountDigits(value: string): string {
  const dot = value.indexOf('.');
  const whole = dot < 0 ? value : value.slice(0, dot);
  const decimals = dot < 0 ? null : value.slice(dot + 1).slice(0, AMOUNT_MAX_DECIMALS);
  let digits = 0;
  let cappedWhole = '';
  for (const char of whole) {
    if (/\d/.test(char)) {
      if (digits >= AMOUNT_MAX_INT_DIGITS) continue;
      digits += 1;
    }
    cappedWhole += char;
  }
  return decimals === null ? cappedWhole : `${cappedWhole}.${decimals}`;
}

/** A trailing empty row appears as soon as the last one is used (Angular `onLineChange`). */
function appendTrailingRow(model: TrialBalanceEditModel, line: TrialBalanceEditLine): void {
  const lines = model.lines;
  if (isLastLine(lines, line) && !isEmptyEditLine(line)) {
    model.lines = [...lines, emptyEditLine()];
  }
}

/** Angular TrialBalanceBackdetailsComponent: the editable lines grid (the totals show in the Overview above). */
export function TrialBalanceBackdetails({
  model,
  onEdit,
  onOpenChartOfAccounts,
  onAddAccount,
  onSave,
  onCancel,
  isSaving = false,
}: TrialBalanceBackdetailsProps) {
  /**
   * Options for the account picker, rebuilt only when the chart or the set of codes on screen changes, so the open
   * dropdown is not handed a fresh array on every keystroke (Angular `accountOptionsKey`).
   */
  const signature = accountOptionsSignature(model);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuilt only when the signature changes, by design
  const accountOptions = useMemo(() => buildAccountOptions(model), [signature]);

  /** Particulars filled in from the chart, so a later code change can refresh them. */
  const autoFilled = useRef(new WeakMap<TrialBalanceEditLine, string>());

  /**
   * Bug (live, 2026-10-19): a line read from import as "not in the client's chart of accounts" whose code the
   * chart does in fact resolve (the chart changed after the import ran) stayed flagged forever, because the
   * Account dropdown is an ng-select: clicking the option that matches the value it already shows - exactly what
   * re-confirming an already-correct code looks like - does not re-emit a change event (ng-select parity: no
   * event unless the value differs), so `onAccountCodeChange` never ran to clear the stale flag or fill
   * Particular. Reconciling here, whenever the chart or the lines change, fixes it without depending on a click
   * that may never fire - for every code, not only ones a user happens to pick a different value for.
   */
  useEffect(() => {
    const stale = (model.lines ?? []).some(
      (l) => l.legacyCodeStatus === 'notInChart' && findAccount(model.accounts, l.accountCode),
    );
    if (!stale) return;
    onEdit((m) => {
      for (const line of m.lines) {
        if (line.legacyCodeStatus !== 'notInChart') continue;
        const account = findAccount(m.accounts, line.accountCode);
        if (!account) continue;
        delete line.legacyCodeStatus;
        delete line.legacyCodeNote;
        line.particular = account.name;
        autoFilled.current.set(line, account.name);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-checks whenever the chart or the coded lines change
  }, [model.accounts, signature]);

  const profitIsClassified = hasClassifiedProfitLines(model);
  /**
   * task-120: the Account picker offers the client's chart, so a client whose chart has never
   * been seeded has nothing to pick - which reads as the picker being broken, and leaves every
   * line unclassified so the profit can only be nil. Say which of the two it is.
   */
  const hasChart = (model.accounts?.length ?? 0) > 0;

  /** #190: accounts suggested from the words typed into a line's Particular. */
  const suggestions = useAccountSuggestions(model.clientId);
  const shown = suggestions.state;
  const suggestionsOpen = shown.row !== null && shown.accounts.length > 0;

  /**
   * BUG-040: "Add Account" creates a missing nominal from the grid, as the old app's journal grid does: the same popup
   * and validations as Chart of Accounts. The chart is read again afterwards so the new account can be picked. With
   * the screen's own handler the popup opens with what was typed and the new account is picked for that line.
   */
  const addAccount = async (index: number, searchTerm: string) => {
    if (onAddAccount) {
      await addAccountFor(index, searchTerm);
      return;
    }
    const options = await trialBalanceService.getAccountOptions(model.clientId);
    const data: AddNewAccountDialogData = { clientId: model.clientId, options };
    const saved = await openDialog(AddNewAccount, { data }).afterClosed();
    if (!saved) return;
    const accounts = await loadChartAccounts(model.clientId);
    onEdit((m) => {
      m.accounts = accounts;
    });
  };

  const editLine = (index: number, change: (line: TrialBalanceEditLine) => void) =>
    onEdit((m) => {
      const line = m.lines[index];
      change(line);
      appendTrailingRow(m, line);
    });

  /**
   * BUG-039: as the old application's grid, a line holds one side: an amount (other than nil) typed into its Debit
   * clears its Credit and the reverse. While typing, a box keeps only an amount it can hold - letters are dropped, one
   * ".", and the decimals are rounded to two only when the box is left (`sanitiseAmountInput`), so 1000.555 is 1000.56.
   * Text with no digits in it (abc) is remembered, and leaving the box shows it as 0.00 as the old grid did.
   */
  const rejectedText = useRef(new Set<string>());
  const typeAmount = (index: number, side: 'debit' | 'credit', typed: string) => {
    const value = capAmountDigits(sanitiseAmountInput(typed));
    const key = `${index}:${side}`;
    if (typed.trim() && !/\d/.test(value)) {
      rejectedText.current.add(key);
    } else {
      rejectedText.current.delete(key);
    }
    editLine(index, (line) => {
      line[side] = value;
      if (parseAmount(value) !== 0) {
        line[side === 'debit' ? 'credit' : 'debit'] = '';
      }
    });
  };

  /**
   * BUG-039: leaving a Debit or Credit box shows the line as it will be stored - two decimals, the side it does not
   * use as 0.00, without pasted commas or "£". Typing is never reformatted while the box has focus. Nothing is queued
   * when the line already shows its stored amounts.
   */
  const normaliseAmountsOf = (index: number, side: 'debit' | 'credit') => {
    const current = model.lines[index];
    if (!current) {
      return;
    }
    const key = `${index}:${side}`;
    const wasText = rejectedText.current.delete(key) && !`${current[side] ?? ''}`.trim();
    const normalise = (line: TrialBalanceEditLine) => {
      if (wasText) {
        line[side] = '0';
      }
      normaliseEditLine(line);
    };
    const shown = { ...current };
    normalise(shown);
    if (shown.debit !== current.debit || shown.credit !== current.credit) {
      editLine(index, normalise);
    }
  };

  /**
   * #192: removes one line. It goes through the same edit as any other change, so the totals, status and profit are
   * recalculated and Save (BUG-036) sends the remaining lines, which replace the saved ones. The grid keeps its empty
   * row to type into.
   */
  const removeLine = (index: number) => {
    // The rows below move up, so an open suggestion list no longer points at its row.
    suggestions.clear();
    onEdit((m) => {
      m.lines = ensureTrailingRows(m.lines.filter((_, i) => i !== index));
    });
  };

  const onAccountCodeChange = (
    index: number,
    code: string | null,
    options: { added?: boolean; keepParticular?: boolean } = {},
  ) =>
    onEdit((m) => {
      const { added = false, keepParticular = false } = options;
      const line = m.lines[index];
      const codeChanged = (code ?? '') !== line.accountCode;
      // Bug: a line flagged "not in the client's chart of accounts" at import keeps whatever text the import gave
      // its Particular (the source file's own description, if any) - never recorded as auto-filled by this
      // handler, so the "don't clobber hand-typed text" guard below mistook it for a deliberate user edit and
      // refused to ever replace it, even when the user picks this exact account specifically to resolve the flag.
      const wasFlaggedNotInChart = line.legacyCodeStatus === 'notInChart';
      if (codeChanged) {
        // BUG-065/067/068: the user has chosen the account, so the read file's old-code note no longer applies.
        delete line.legacyCodeStatus;
        delete line.legacyCodeNote;
      }
      if (codeChanged || added) {
        // FRS import (14-Oct): TB profit - nor does the area the server gave the line: the chosen (or added) account decides.
        forgetLineClassification(line);
      }
      line.accountCode = code ?? '';
      const account = findAccount(m.accounts, line.accountCode);
      if (account) {
        // Bug: re-picking the exact code already on the line (e.g. confirming a "not in chart" line whose code is
        // in fact valid) left the stale warning in place because the code text never changed. Whether the account
        // is now found in the chart - not whether the code text differs from before - decides whether the warning
        // still applies.
        delete line.legacyCodeStatus;
        delete line.legacyCodeNote;
      }
      if (!keepParticular) {
        const autoValue = autoFilled.current.get(line);
        const particularIsAuto = autoValue !== undefined && line.particular === autoValue;
        if (account) {
          if (!line.particular?.trim() || particularIsAuto || wasFlaggedNotInChart) {
            line.particular = account.name;
            autoFilled.current.set(line, account.name);
          }
        } else if (particularIsAuto) {
          line.particular = '';
          autoFilled.current.delete(line);
        }
      }
      appendTrailingRow(m, line);
    });

  /** BUG-040: a line whose search found no account gets the account added for it, exactly as if it had been picked. */
  const addAccountFor = async (index: number, searchTerm: string) => {
    const code = await onAddAccount?.(searchTerm);
    if (code) {
      onAccountCodeChange(index, code, { added: true });
    }
  };

  /**
   * Choosing a suggestion sets the line's account exactly as picking it in the Account dropdown does, except the
   * Particular is left alone - it is the text the user just typed to find the suggestion, not stale import data,
   * so there is nothing to refresh (Angular/TrialBalancePanels: "leaves its particular, debit and credit alone").
   */
  const chooseSuggestion = (accountId: string) => {
    const row = shown.row;
    const account = shown.accounts.find((a) => a.id === accountId);
    suggestions.clear();
    if (row !== null && account) {
      onAccountCodeChange(row, account.code, { keepParticular: true });
    }
  };

  /** The Particular keeps focus while the list is open: arrows move through it, Enter chooses, Escape closes. */
  const onParticularKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (!suggestionsOpen || shown.row !== index) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      suggestions.setActive(Math.min(Math.max(shown.active + step, 0), shown.accounts.length - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      chooseSuggestion(shown.accounts[shown.active].id);
    } else if (event.key === 'Escape') {
      suggestions.clear();
    }
  };

  const columns: DataTableColumn<TrialBalanceEditLine>[] = [
    {
      id: 'account',
      header: 'Account',
      headerClassName: 'w-auto',
      cell: (element, index) => (
        <>
          <Select<AccountPickOption>
            className="wp-100"
            placeholder="Search code or name"
            notFoundText={
              hasChart
                ? 'No matching account'
                : 'This client has no chart of accounts yet. Add it under Settings > Chart of Accounts.'
            }
            items={accountOptions}
            bindLabel="searchText"
            bindValue="code"
            searchable
            value={element.accountCode}
            onChange={(value) => onAccountCodeChange(index, value as string | null)}
            notFoundAction={
              onAddAccount &&
              (({ searchTerm, close }) => (
                <Button
                  className="link-button d-flex align-items-center"
                  onClick={() => {
                    close();
                    void addAccountFor(index, searchTerm);
                  }}
                >
                  <Icon name="add" className="font-size-18" />
                  Add New Account
                </Button>
              ))
            }
            renderLabel={(item) => (
              <>
                {item.code} - {item.name}
              </>
            )}
            renderOption={(item) => (
              <>
                {item.code} - {item.name}
                {item.accountType && <span className="text-gray"> ({item.accountType})</span>}
              </>
            )}
          />
          {element.legacyCodeNote && (
            <div className={`tb-legacy-note font-size-12 tb-legacy-note--${element.legacyCodeStatus ?? 'translated'}`}>
              {element.legacyCodeNote}
            </div>
          )}
        </>
      ),
    },
    {
      id: 'particular',
      header: 'Particular',
      headerClassName: 'w-auto',
      cell: (element, index) => (
        <>
          <InputField
            fieldClassName="wp-100"
            charMode="alphanumeric"
            maxLength={255}
            value={element.particular}
            aria-autocomplete="list"
            onChange={(event) => {
              const value = event.target.value;
              const anchor = event.currentTarget.closest<HTMLElement>('.mat-mdc-form-field') ?? event.currentTarget;
              editLine(index, (line) => {
                line.particular = value;
              });
              suggestions.search(index, value, anchor);
            }}
            onKeyDown={(event) => onParticularKeyDown(event, index)}
          />
          {shown.row === index && shown.searched && shown.accounts.length === 0 && (
            <div className="text-gray font-size-12">
              No matching accounts in the chart of accounts.{' '}
              <a className="link hyperlink" onClick={() => void addAccount(index, element.particular ?? '')}>
                Add Account
              </a>
            </div>
          )}
        </>
      ),
    },
    {
      id: 'debit',
      header: 'Debit (£ - GBP)',
      headerClassName: 'w-150',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-150"
          inputMode="decimal"
          value={element.debit}
          data-tb-amount-line={index}
          onChange={(event) => typeAmount(index, 'debit', event.target.value)}
          onBlur={() => normaliseAmountsOf(index, 'debit')}
        />
      ),
    },
    {
      id: 'credit',
      header: 'Credit (£ - GBP)',
      headerClassName: 'w-150',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-150"
          inputMode="decimal"
          value={element.credit}
          data-tb-amount-line={index}
          onChange={(event) => typeAmount(index, 'credit', event.target.value)}
          onBlur={() => normaliseAmountsOf(index, 'credit')}
        />
      ),
    },
    {
      id: 'delete',
      header: '',
      headerClassName: 'w-50',
      // An empty row has nothing to delete; it is where the next line is typed.
      cell: (element, index) =>
        isEmptyEditLine(element) ? null : (
          <Button className="close-icn" title="Delete line" aria-label="Delete line" onClick={() => removeLine(index)}>
            <Icon name="close" outlined />
          </Button>
        ),
    },
  ];

  return (
    <div className="sidenav-body-content p-0 mt-1 tr-tb-backdetails">
      <div className="wp-100">
        {/* APT354: no "Back to Details" and no Ref. No. / Description / Period strip here - the tabs and the details above carry them. */}
        <div className="sidenav-table sidenav-table-input wp-100 hr-top mt-1 mb-1 pt-1">
          <div className="mat-table-wrapper contact-detail-table">
            <DataTable columns={columns} rows={model.lines} rowKey={(_, index) => index} stickyHeader />
          </div>
          {onAddAccount && (
            <div className="mt-1">
              <a className="link text-underline-none d-flex align-items-center" onClick={() => void onAddAccount('')}>
                <Icon name="add" className="font-size-18" />
                Add Account
              </a>
            </div>
          )}
          {/* #190: the Account dropdown's own list style; nothing is chosen until the user picks one. */}
          <SelectPanel
            anchorEl={shown.anchor}
            open={suggestionsOpen}
            onClose={() => suggestions.clear()}
            options={shown.accounts.map((account) => ({
              key: account.id,
              label: `${account.code} - ${account.accountName}${account.category ? ` (${account.category})` : ''}`,
              selected: false,
            }))}
            onSelect={(option) => chooseSuggestion(option.key)}
            ariaLabel="Account suggestions"
            exactWidth={false}
            keepFocus
            disableRestoreFocus
            activeIndex={shown.active}
            onActiveIndexChange={suggestions.setActive}
          />
        </div>
        {/*
          Ticket "Saving TB": Total Dr., Total Cr., Status and the profit already show in the Overview above, so they are
          not repeated under the lines; only the note on unrecognised profit and loss codes stays.
        */}
        {!profitIsClassified && (
          <p className="text-gray mb-0 mt-1">
            {hasChart ? (
              'No profit and loss codes recognised yet.'
            ) : (
              <>
                This client has no chart of accounts, so no line can be recognised as profit and loss.{' '}
                <Button className="link-button" onClick={onOpenChartOfAccounts}>
                  Open Chart of Accounts
                </Button>
              </>
            )}
          </p>
        )}
        <TrialBalanceActions onCancel={onCancel} onSave={onSave} isSaving={isSaving} />
      </div>
    </div>
  );
}
